import * as _ from 'lodash';
import { useMemo, useState } from 'react';

import { TEAM_GROUPS } from '../../../../shared/featureFlags/flagNames';
import { RoleScope } from '../../../../typings/enums';
import { Action, ResourceType } from '../../../../typings/permissions';
import { useDialogContext } from '../../../contexts/DialogContext';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { orgRolesList } from '../../../helpers/roles';
import { useToggle } from '../../../hooks';
import useAssignableRoles from '../../../hooks/api/roles/useAssignableRoles';
import useRoles from '../../../hooks/api/roles/useRoles';
import useTeamGroups from '../../../hooks/api/teams/teamGroups/useTeamGroups';
import useTeams from '../../../hooks/api/teams/useTeams';
import useCreateServiceAccount from '../../../hooks/api/users/useCreateServiceAccount';
import useHasPermission from '../../../shared/permissions/useHasPermission';
import { ErrorAlert } from '../../generic';
import { BUTTON_TYPE } from '../../generic/Button';
import TooltipButton from '../../generic/Button/TooltipButton';
import CopyToClipboard from '../../generic/CopyToClipboard';
import Flex from '../../generic/Flex';
import FormLabel from '../../generic/Forms/FormLabel';
import LabeledCheckbox from '../../generic/Forms/LabeledCheckbox';
import LabeledSelect from '../../generic/Forms/LabeledSelect';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import TextInput from '../../generic/Forms/TextInput';
import Loader from '../../generic/Loader';
import Text from '../../generic/Text';
import Header from '../../shared/settings/SettingsPageHeader';
import Section from '../../shared/settings/SettingsPageSection';
import OrganizationSettingsPermissionGate from '../OrganizationSettingsPermissionGate';
import { useCanManageServiceAccounts } from '../shared/permissions';

import styles from './apiTokens/apiTokens.module.less';

type SelectOption = {
  value: number | null;
  label: string;
};

const NoneSelectOption: SelectOption = {
  value: null,
  label: 'None',
};

const CreateApiUserSettingsContent = () => {
  const hasPermission = useHasPermission();
  const canManageAllServiceAccounts = hasPermission({
    action: Action.Manage,
    resourceType: ResourceType.ServiceAccount,
  });

  const { onConfirm } = useDialogContext();
  const isTeamGroupsFeatureEnabled = useIsFeatureEnabled(TEAM_GROUPS);

  const [fullName, setFullName] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [roleId, setRoleId] = useState<SelectOption>(NoneSelectOption);
  const [teamId, setTeamId] = useState<SelectOption>(NoneSelectOption);
  const [teamRoleId, setTeamRoleId] = useState<SelectOption>(NoneSelectOption);
  const [shouldCreatePushOnlyApiToken, toggleCreatePushOnlyApiToken] = useToggle(false);
  const [shouldCreateFullApiToken, toggleCreateFullApiToken] = useToggle(false);

  const { mutateAsync: createServiceAccount, isPending, error } = useCreateServiceAccount();

  const { data: teams, isLoading: isLoadingTeams } = useTeams();
  const { data: teamGroups, isLoading: isLoadingTeamGroups } = useTeamGroups(isTeamGroupsFeatureEnabled);
  const { data: roles, isLoading: isLoadingRoles } = useRoles();
  const { data: assignableRoles, isLoading: isLoadingAssignableRoles } = useAssignableRoles();
  const keyedRoles = _.keyBy(roles ?? [], (role) => role.id);

  const teamsList = useMemo(() => {
    const list: SelectOption[] = [...(teams ?? []), ...(teamGroups ?? [])]
      .filter(
        (team) =>
          canManageAllServiceAccounts ||
          hasPermission({
            action: Action.Manage,
            resourceType: ResourceType.ServiceAccountTeam,
            teamId: team.id,
          }),
      )
      .map((team) => ({
        value: team.id,
        label: team.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    list.unshift(NoneSelectOption);

    return list;
  }, [teams, teamGroups, canManageAllServiceAccounts, hasPermission]);
  const teamRolesList = useMemo(() => {
    const list: SelectOption[] = [...(roles ?? [])]
      .filter((role) => role.scope === RoleScope.Team)
      .map((role) => ({
        value: role.id,
        label: role.name,
        isDisabled: !!teamId.value && !assignableRoles?.assignableTeamRoles[teamId.value]?.includes(role.id),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    list.unshift(NoneSelectOption);

    return list;
  }, [assignableRoles?.assignableTeamRoles, roles, teamId.value]);

  const isLoading =
    isLoadingTeams
    || (isTeamGroupsFeatureEnabled && isLoadingTeamGroups)
    || isLoadingRoles
    || isLoadingAssignableRoles;

  // Validation: Check if required fields are populated
  const isFormValid = useMemo(() => {
    // Username is required
    if (!username.trim()) {
      return false;
    }

    // At least one of orgRoleId or team must be specified
    const hasOrgRole = roleId.value !== null;
    const hasTeam = teamId.value != null && teamRoleId.value != null;

    return hasOrgRole || hasTeam;
  }, [username, roleId.value, teamId.value, teamRoleId.value]);

  // Generate validation message for tooltip
  const validationMessage = useMemo(() => {
    const missingFields: string[] = [];

    if (!username.trim()) {
      missingFields.push('Username');
    }

    const hasOrgRole = roleId.value !== null;
    const hasTeam = teamId.value !== null && teamRoleId?.value !== null;

    if (!hasOrgRole && !hasTeam) {
      missingFields.push('Organization Role or Team with Team Role');
    }

    if (missingFields.length === 0) {
      return null;
    }

    return `Missing required fields: ${missingFields.join(', ')}`;
  }, [username, roleId.value, teamId.value, teamRoleId?.value]);

  const onCreateUser = async () => {
    try {
      const response = await createServiceAccount({
        username,
        email: email || undefined,
        fullName: fullName || undefined,
        orgRoleId: roleId.value ?? undefined,
        team:
          teamId.value && teamRoleId.value
            ? {
                id: teamId.value,
                roleId: teamRoleId.value,
              }
            : undefined,
        hasPushOnlyApiToken: shouldCreatePushOnlyApiToken,
        hasFullApiToken: shouldCreateFullApiToken,
      });

      const data = response.data;
      const hasTokens = data?.fullApiToken || data?.pushOnlyApiToken;

      onConfirm({
        title: 'New Service Account Created',
        onConfirm: _.noop,
        confirmBtnType: BUTTON_TYPE.PRIMARY,
        body: (
          <div>
            {hasTokens ? (
              <p>
                A new service account has been created. Please copy{' '}
                {data.fullApiToken && data.pushOnlyApiToken ? 'these tokens' : 'the token'} below. The token
                {data.fullApiToken && data.pushOnlyApiToken ? 's' : ''} will never be displayed again.
              </p>
            ) : (
              <p>A new service account has been created successfully.</p>
            )}
            {data?.fullApiToken && (
              <div className={styles.tokenLabel}>
                <FormLabel label="Full API Token" />
                <TextInput
                  label="token-label"
                  value={data.fullApiToken}
                  suffix={<CopyToClipboard text={data.fullApiToken} />}
                />
              </div>
            )}
            {data?.pushOnlyApiToken && (
              <div className={styles.tokenLabel}>
                <FormLabel label="Push-Only API Token" />
                <TextInput
                  label="token-label"
                  value={data.pushOnlyApiToken}
                  suffix={<CopyToClipboard text={data.pushOnlyApiToken} />}
                />
              </div>
            )}
          </div>
        ),
      });

      // Reset form
      setUsername('');
      setFullName('');
      setEmail('');
      setRoleId(NoneSelectOption);
      setTeamId(NoneSelectOption);
      setTeamRoleId(NoneSelectOption);
      if (shouldCreatePushOnlyApiToken) {
        toggleCreatePushOnlyApiToken();
      }
      if (shouldCreateFullApiToken) {
        toggleCreateFullApiToken();
      }
    } catch (e) {
      // Error will be shown in the `ErrorAlert` component
    }
  };

  if (isLoading) {
    return <Loader size={80} />;
  }

  return (
    <>
      {error && <ErrorAlert status={{ error: error.data.message }} />}
      <Header heading="Service Accounts" />
      <Section>
        <Section.Left heading="Create a Service Account">
          <Text.Text>
            Create a special user that can be used to interact with the FOSSA API using either an API Token or OIDC for
            authentication. Service accounts are not affected by the organization&rsquo;s SSO configuration and can not
            log in using a username and password.
          </Text.Text>
        </Section.Left>
        <Section.Right>
          <LabeledTextInput labelVariant="legacy" label="Username (required)" value={username} onChange={setUsername} />
          <div className={styles.flexRow}>
            <LabeledTextInput labelVariant="legacy" label="Name" value={fullName} onChange={setFullName} />
            <LabeledTextInput labelVariant="legacy" label="Email" value={email} onChange={setEmail} />
          </div>
          <LabeledSelect
            variant="legacy"
            label="Organization Role"
            id="new-user-org-role-dropdown"
            options={orgRolesList(keyedRoles).map((role) => ({
              label: role.name,
              value: role.id,
              isDisabled: !!role.id && !assignableRoles?.assignableOrgRoles.includes(role.id),
            }))}
            value={roleId}
            onChange={(role) => setRoleId(role ?? NoneSelectOption)}
          />
          <div className={styles.flexRow}>
            <LabeledSelect
              variant="legacy"
              label="Team"
              id="new-user-team-dropdown"
              options={teamsList}
              value={teamId}
              onChange={(team) => {
                if (team?.value !== teamId.value) {
                  setTeamId(team ?? NoneSelectOption);
                  setTeamRoleId(NoneSelectOption);
                }
              }}
            />
            <LabeledSelect
              variant="legacy"
              label="Team Role"
              id="new-user-team-role-dropdown"
              options={teamRolesList}
              value={teamRoleId}
              onChange={(teamRole) => setTeamRoleId(teamRole ?? NoneSelectOption)}
              isDisabled={!teamId.value}
            />
          </div>
          <LabeledCheckbox
            label="Create a push-only API token"
            isChecked={shouldCreatePushOnlyApiToken}
            onChange={toggleCreatePushOnlyApiToken}
            isRippleDisabled
          />
          <br />
          <LabeledCheckbox
            label="Create a full API token"
            isChecked={shouldCreateFullApiToken}
            onChange={toggleCreateFullApiToken}
            isRippleDisabled
          />
          <Flex justify="flex-end">
            <TooltipButton
              id="create-service-account-button"
              type={BUTTON_TYPE.PRIMARY}
              onClick={onCreateUser}
              isDisabled={!isFormValid || isPending}
              tooltip={validationMessage || ''}
              showTooltip={!isFormValid}
            >
              Create
            </TooltipButton>
          </Flex>
        </Section.Right>
      </Section>
    </>
  );
};

const CreateApiUserSettingsContainer = () => {
  const canManageServiceAccounts = useCanManageServiceAccounts();
  return (
    <OrganizationSettingsPermissionGate checkPermission={() => canManageServiceAccounts}>
      <CreateApiUserSettingsContent />
    </OrganizationSettingsPermissionGate>
  );
};

export default CreateApiUserSettingsContainer;
